import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Play, Pause, SkipBack, SkipForward, Repeat, Volume2, Rewind,
  Heart, ArrowLeft, Loader2, Gauge,
} from "lucide-react";
import api from "@/lib/api";
import useAudioPlayer from "@/lib/useAudioPlayer";
import { API } from "@/lib/api";
import Waveform from "@/components/Waveform";
import MixerStrip from "@/components/MixerStrip";
import LyricsPanel from "@/components/LyricsPanel";
import ChordTimeline from "@/components/ChordTimeline";
import StructureTimeline from "@/components/StructureTimeline";
import PracticePanel from "@/components/PracticePanel";
import ExportMenu from "@/components/ExportMenu";
import { AiBadge, DemoBadge, ConfidenceBadge } from "@/components/Badges";
import { fmtTime, SATB, INSTRUMENTS, transposeKey, computeBandGains } from "@/lib/music";

const TABS = [
  { key: "vocals", label: "Vocal Mixer" },
  { key: "instruments", label: "Instruments" },
  { key: "lyrics", label: "Lyrics" },
  { key: "chords", label: "Chords" },
  { key: "structure", label: "Structure" },
  { key: "practice", label: "Practice" },
];

export default function Studio() {
  const { songId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(params.get("tab") || "vocals");
  const [transpose, setTranspose] = useState(0);
  const [favorite, setFavorite] = useState(false);
  const [vocalMix, setVocalMix] = useState({});
  const [instMix, setInstMix] = useState({});
  const [looping, setLooping] = useState(false);

  const src = `${API}/songs/${songId}/audio`;
  const player = useAudioPlayer(src);
  const setBands = player.setBands;

  useEffect(() => {
    api.get(`/songs/${songId}`).then(({ data }) => {
      setData(data);
      setFavorite(data.song.favorite);
    });
  }, [songId]);

  // initialize mixer states (all tracks at full volume)
  useEffect(() => {
    const init = (tracks) => Object.fromEntries(tracks.map((t) => [t.key, { volume: 1, muted: false, solo: false }]));
    setVocalMix(init(SATB));
    setInstMix(init(INSTRUMENTS));
  }, []);

  // apply mixer settings to the audio (demo 3-band EQ) based on active mixer tab
  useEffect(() => {
    if (tab === "vocals" && Object.keys(vocalMix).length) setBands(computeBandGains(SATB, vocalMix));
    else if (tab === "instruments" && Object.keys(instMix).length) setBands(computeBandGains(INSTRUMENTS, instMix));
  }, [tab, vocalMix, instMix, setBands]);

  const sections = useMemo(() => data?.sections?.sections || [], [data]);
  const duration = player.duration || data?.song?.duration || 0;

  const currentSectionIdx = useMemo(
    () => sections.findIndex((s) => player.currentTime >= s.start && player.currentTime < s.end),
    [sections, player.currentTime]
  );

  const stemConf = useMemo(() => {
    const m = {};
    (data?.stems || []).forEach((s) => { m[s.stem_type] = s.confidence; });
    return m;
  }, [data]);

  if (!data) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={30} /></div>;
  }

  const { song } = data;
  const analysis = song.analysis || {};

  const toggleFav = async () => {
    const { data } = await api.post(`/songs/${songId}/favorite`);
    setFavorite(data.favorite);
  };

  const gotoSection = (dir) => {
    if (!sections.length) return player.seek(player.currentTime + dir * 10);
    let idx = currentSectionIdx;
    idx = dir > 0 ? Math.min(sections.length - 1, idx + 1) : Math.max(0, idx - 1);
    player.seek(sections[idx].start);
  };

  const toggleLoopSection = () => {
    if (looping) { player.setLoop(null); setLooping(false); }
    else if (sections[currentSectionIdx]) {
      player.setLoop({ start: sections[currentSectionIdx].start, end: sections[currentSectionIdx].end });
      setLooping(true);
    }
  };

  return (
    <div className="space-y-5 pb-40 sw-fade-up md:pb-28">
      <audio ref={player.audioRef} src={src} preload="metadata" />

      {/* header */}
      <div className="flex items-start gap-3">
        <button data-testid="studio-back" onClick={() => nav(-1)} className="rounded-full bg-[#131822] p-2 text-slate-300 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate font-display text-2xl font-extrabold text-white">{song.title}</h1>
            {analysis.is_demo && <DemoBadge />}
          </div>
          <p className="truncate text-sm text-slate-400">{song.artist || "Unknown artist"}{song.genre ? ` · ${song.genre}` : ""}</p>
        </div>
        <button data-testid="studio-favorite" onClick={toggleFav} className="rounded-full bg-[#131822] p-2">
          <Heart size={18} className={favorite ? "fill-rose-500 text-rose-500" : "text-slate-400"} />
        </button>
        <ExportMenu
          songId={songId}
          songTitle={song.title}
          transpose={transpose}
          hasChords={!!(data.chords?.progression?.length)}
          hasLyrics={!!(data.lyrics?.segments?.length)}
        />
      </div>

      {/* metadata chips */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MetaChip label="Duration" value={fmtTime(duration)} />
        <MetaChip label="Key" value={transposeKey(analysis.key?.value, transpose) || "—"} conf={analysis.key?.confidence} />
        <MetaChip label="BPM" value={analysis.bpm?.value || "—"} conf={analysis.bpm?.confidence} />
        <MetaChip label="Time Sig" value={analysis.time_signature?.value || "—"} />
      </div>

      {/* waveform */}
      <Waveform
        seed={songId}
        currentTime={player.currentTime}
        duration={duration}
        onSeek={player.seek}
        sections={sections}
        loop={looping && sections[currentSectionIdx] ? sections[currentSectionIdx] : null}
      />

      {/* tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`studio-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#131822] text-slate-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* tab content */}
      <div>
        {tab === "vocals" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200/80">
              <AiBadge /> Soprano, Alto, Tenor &amp; Bass are AI-estimated. In DEMO mode the faders, mute &amp; solo drive a live 3-band EQ preview (true isolated stems require a connected separation API). Press play to hear changes.
            </div>
            {SATB.map((t) => (
              <MixerStrip key={t.key} track={t} state={vocalMix[t.key]} playing={player.playing} confidence={stemConf[t.key]}
                onChange={(st) => setVocalMix((m) => ({ ...m, [t.key]: st }))} />
            ))}
          </div>
        )}
        {tab === "instruments" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-200/80">
              <DemoBadge /> Instrument stems are AI-estimated. In DEMO mode the faders, mute &amp; solo drive a live 3-band EQ preview. Press play to hear changes.
            </div>
            {INSTRUMENTS.map((t) => (
              <MixerStrip key={t.key} track={t} state={instMix[t.key]} playing={player.playing} confidence={stemConf[t.key]}
                onChange={(st) => setInstMix((m) => ({ ...m, [t.key]: st }))} />
            ))}
          </div>
        )}
        {tab === "lyrics" && (
          <LyricsPanel segments={data.lyrics?.segments || []} currentTime={player.currentTime} onSeek={player.seek} isDemo={data.lyrics?.is_demo} />
        )}
        {tab === "chords" && (
          <ChordTimeline chordData={data.chords} currentTime={player.currentTime} onSeek={player.seek}
            transpose={transpose} setTranspose={setTranspose} songKey={analysis.key} />
        )}
        {tab === "structure" && (
          <StructureTimeline sectionsData={data.sections} currentTime={player.currentTime} onSeek={player.seek} />
        )}
        {tab === "practice" && (
          <PracticePanel song={song} sections={sections} bpm={analysis.bpm} player={player}
            transpose={transpose} setTranspose={setTranspose} currentTime={player.currentTime} />
        )}
      </div>

      {/* sticky transport */}
      <div className="fixed inset-x-0 bottom-[68px] z-30 border-t border-[#222A38] bg-[#0B0E14]/95 px-4 py-3 backdrop-blur-xl md:bottom-0 md:left-60">
        <div className="mx-auto max-w-5xl">
          <div className="mb-2 flex items-center gap-3">
            <span className="font-mono-sw text-xs text-slate-400">{fmtTime(player.currentTime)}</span>
            <input
              data-testid="seek-bar"
              type="range" min="0" max={duration || 0} step="0.1" value={player.currentTime}
              onChange={(e) => player.seek(parseFloat(e.target.value))}
              className="sw-slider-track h-1.5 w-full"
            />
            <span className="font-mono-sw text-xs text-slate-400">{fmtTime(duration)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button data-testid="prev-section" onClick={() => gotoSection(-1)} className="rounded-full p-2 text-slate-300 hover:text-white"><SkipBack size={18} /></button>
              <button data-testid="rewind" onClick={() => player.seek(Math.max(0, player.currentTime - 10))} className="rounded-full p-2 text-slate-300 hover:text-white"><Rewind size={18} /></button>
              <button data-testid="play-pause-btn" onClick={player.toggle} className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-500 text-[#0B0E14] shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-transform hover:scale-105">
                {player.playing ? <Pause size={22} className="fill-[#0B0E14]" /> : <Play size={22} className="fill-[#0B0E14] pl-0.5" />}
              </button>
              <button data-testid="next-section" onClick={() => gotoSection(1)} className="rounded-full p-2 text-slate-300 hover:text-white"><SkipForward size={18} /></button>
              <button
                data-testid="loop-section-btn"
                onClick={toggleLoopSection}
                className={`rounded-full p-2 ${looping ? "text-cyan-400" : "text-slate-300 hover:text-white"}`}
              >
                <Repeat size={18} />
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1.5">
                <Gauge size={15} className="hidden text-slate-400 xs:block sm:block" />
                <select
                  data-testid="speed-select"
                  value={player.rate}
                  onChange={(e) => player.setRate(parseFloat(e.target.value))}
                  className="rounded-lg border border-[#222A38] bg-[#131822] px-2 py-1 text-xs text-white outline-none"
                >
                  {[0.25, 0.5, 0.75, 1, 1.25, 1.5].map((r) => <option key={r} value={r}>{r}x</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <Volume2 size={15} className="text-slate-400" />
                <input
                  data-testid="volume-slider"
                  type="range" min="0" max="1" step="0.01" value={player.volume}
                  onChange={(e) => player.setVolume(parseFloat(e.target.value))}
                  className="sw-slider-track h-1.5 w-14 sm:w-24"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaChip({ label, value, conf }) {
  return (
    <div className="rounded-xl border border-[#222A38] bg-[#131822] p-3">
      <div className="flex items-center justify-between">
        <span className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        {conf != null && <ConfidenceBadge value={conf} />}
      </div>
      <div className="mt-0.5 font-display text-lg font-extrabold text-white">{value}</div>
    </div>
  );
}
